/**
 * KPI Cards - Static summary metrics at the top of the dashboard.
 * These are not customizable/draggable.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
} from "lucide-react";

interface KpiCardsProps {
  kpis: {
    nav: { value: number | null; change: number | null };
    ytdReturn: { value: number | null; dollarValue: number | null };
    quarterReturn: { value: number | null; dollarValue: number | null; quarter: string };
    todayReturn: { value: number | null; dollarValue: number | null; change: number | null };
    feesYTD: { value: number | null; breakdown: { mgmt: number } };
    cash: { value: number | null };
  };
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function KpiCards({ kpis }: KpiCardsProps) {
  const navigate = useNavigate();

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/positions')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total NAV</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.nav.value !== null ? (
            <>
              <div className="text-2xl font-bold">{formatCurrency(kpis.nav.value)}</div>
              {kpis.nav.change !== null ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  {kpis.nav.change > 0 ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-destructive" />
                  )}
                  <span className={kpis.nav.change > 0 ? "text-success" : "text-destructive"}>
                    {kpis.nav.change > 0 ? "+" : ""}{kpis.nav.change}%
                  </span>
                  <span>vs prior period</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">As of latest period</p>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground mt-1">No position data</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/positions')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Return YTD</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.ytdReturn.value !== null ? (
            <>
              <div className={`text-2xl font-bold ${kpis.ytdReturn.value >= 0 ? 'text-success' : 'text-destructive'}`}>
                {kpis.ytdReturn.value > 0 ? "+" : ""}{kpis.ytdReturn.value.toFixed(1)}%
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                {kpis.ytdReturn.dollarValue !== null && kpis.ytdReturn.dollarValue > 0 ? "+" : ""}
                {kpis.ytdReturn.dollarValue !== null ? formatCurrency(kpis.ytdReturn.dollarValue) : "--"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Year to Date</p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">--</p>
              <p className="text-xs text-muted-foreground mt-1">Year to Date</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/positions')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Return This Quarter</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.quarterReturn.value !== null ? (
            <>
              <div className="text-2xl font-bold">
                {kpis.quarterReturn.value > 0 ? "+" : ""}{kpis.quarterReturn.value.toFixed(1)}%
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                {kpis.quarterReturn.dollarValue !== null && kpis.quarterReturn.dollarValue > 0 ? "+" : ""}
                {kpis.quarterReturn.dollarValue !== null ? formatCurrency(kpis.quarterReturn.dollarValue) : "--"}
              </p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">--</p>
            </>
          )}
          <p className="text-xs text-muted-foreground mt-1">{kpis.quarterReturn.quarter}</p>
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/positions')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Return Today</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.todayReturn.value !== null ? (
            <>
              <div className="text-2xl font-bold">
                {kpis.todayReturn.value > 0 ? "+" : ""}{kpis.todayReturn.value.toFixed(1)}%
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                {kpis.todayReturn.dollarValue !== null && kpis.todayReturn.dollarValue > 0 ? "+" : ""}
                {kpis.todayReturn.dollarValue !== null ? formatCurrency(kpis.todayReturn.dollarValue) : "--"}
              </p>
              {kpis.todayReturn.change !== null && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  {kpis.todayReturn.change > 0 ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-destructive" />
                  )}
                  <span className={kpis.todayReturn.change > 0 ? "text-success" : "text-destructive"}>
                    Daily change
                  </span>
                </p>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">--</p>
              <p className="text-xs text-muted-foreground mt-1">No daily data</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/fees')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Fees YTD</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.feesYTD.value !== null ? (
            <>
              <div className="text-2xl font-bold">{formatCurrency(kpis.feesYTD.value)}</div>
              <p className="text-xs text-muted-foreground">
                Mgmt: {formatCurrency(kpis.feesYTD.breakdown.mgmt)}
              </p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">No fee data</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/liquidity')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Cash</CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.cash.value !== null ? (
            <>
              <div className="text-2xl font-bold">{formatCurrency(kpis.cash.value)}</div>
              <p className="text-xs text-muted-foreground">Available liquidity</p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">No cash data</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
